import { ReactElement, useRef, useState, useEffect } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useIMask } from "react-imask";

import CustomSelect from '@components/CustomSelect';
import Checkbox from '@components/Checkbox';
import TextBanner from '@components/TextBanner';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';

import { mergeRefs, getLabelFallback, validateDate, loadScript, getFormattedPhone, getMd5Phone, getSha256Phone, onKeyPressOnlyNumber, onKeyPressEmail, onKeyPressAlpha, onKeyPressAlphaNumeric } from '@utils/utils';
import { states, smartGlassesDenied, FIELD_PATTERN } from '@utils/const';

import Error from '@images/svg-icons/error.svg';
import Success from '@images/svg-icons/success.svg';
import CalendarIcon from '@images/svg-icons/calendar.svg';
import InfoIcon from '@images/svg-icons/info20.svg';

import * as actions from '@redux-actions/';

type DeliveryAddressFormProps = {
    onSubmitAddress: (value) => void;
    buyerName: string | null;
    addressErrors: string;
}

type FormValues = {
    [key: string]: string;
};


const DeliveryAddressForm = (props: DeliveryAddressFormProps): ReactElement => {

    const {
        onSubmitAddress,
        buyerName,
        addressErrors
    } = props;
    const dispatch = useDispatch();

    let autoComplete;
    let autoComplete2;

    const imaskPhoneNumber = useIMask({ mask: '(000) 000-0000' });
    const deliveryZipCode = useRef(null);
    const billingZipCode = useRef(null);
    const deliveryPhone = useRef(null);

    const config = useSelector(state => state.cart.config);
    const formattedOrderItems = useSelector(state => state.cart.formattedOrderItems);
    const address = useSelector(state => state.cart.address);
    const allBillingAddresses = useSelector(state => state.cart.allBillingAddresses);
    const cart = useSelector(state => state.cart.cart);
    const selectedShippingMode = useSelector(state => state.cart.selectedShippingMode);

    const defaultValues = getDefaultValues(address, cart);
    const [checked, setChecked] = useState(getCheckboxValue(address));

    const [smsChecked, setSmsChecked] = useState(getSmsCheckboxValue());

    const { register, watch, handleSubmit, formState, formState: { errors }, setValue, unregister, trigger, reset, setError, clearErrors, control } = useForm<FormValues>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        defaultValues: defaultValues,
        shouldFocusError: false
    });

    const watchDeliveryZipCode = watch("deliveryZipCode", defaultValues['deliveryZipCode']);

    const methods = useForm<any>();

    const [defaultValueDeliveryState, setDefaultValueDeliveryState] = useState(null);
    const [defaultValueBillingState, setDefaultValueBillingState] = useState(null);
    const [showOptionalDeliveryAddress, setShowOptionalDeliveryAddress] = useState(false);
    const [showOptionalBillingAddress, setShowOptionalBillingAddress] = useState(false);
    const [hasError, setHasError] = useState('');
    const [billingAddresses, setBillingAddresses] = useState([]);
    const [addRef, setAddRef] = useState(true);
    const [birthdayValue, setBirthdayValue] = useState(defaultValues['deliveryBirthday']);
    const [focusedField, setFocusedField] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState('');

    const [isValidEmail, setIsValidEmail] = useState(!errors['deliveryEmail'] && !!defaultValues['deliveryEmail']);
    const [isValidDeliveryFirstName, setIsValidDeliveryFirstName] = useState(!errors['deliveryFirstName'] && !!defaultValues['deliveryFirstName']);
    const [isValidDeliveryLastName, setIsValidDeliveryLastName] = useState(!errors['deliveryLastName'] && !!defaultValues['deliveryLastName']);
    const [isValidDeliveryBirthday, setIsValidDeliveryBirthday] = useState(!errors['deliveryBirthday'] && !!defaultValues['deliveryBirthday']);
    const [isValidDeliveryPhone, setIsValidDeliveryPhone] = useState(!errors['deliveryPhone'] && !!defaultValues['deliveryPhone']);
    const [isValidDeliveryAddress, setIsValidDeliveryAddress] = useState(!errors['deliveryAddress'] && !!defaultValues['deliveryAddress']);
    const [isValidDeliveryAddress2, setIsValidDeliveryAddress2] = useState(!errors['deliveryAddress2'] && !!defaultValues['deliveryAddress2']);
    const [isValidDeliveryZipCode, setIsValidDeliveryZipCode] = useState(!errors['deliveryZipCode'] && !!defaultValues['deliveryZipCode']);
    const [isValidDeliveryCity, setIsValidDeliveryCity] = useState(!errors['deliveryCity'] && !!defaultValues['deliveryCity']);
    const [isValidDeliveryState, setIsValidDeliveryState] = useState(!errors['deliveryState'] && !!defaultValues['deliveryState']);

    const [isValidBillingFirstName, setIsValidBillingFirstName] = useState(!errors['billingFirstName'] && !!defaultValues['billingFirstName']);
    const [isValidBillingLastName, setIsValidBillingLastName] = useState(!errors['billingLastName'] && !!defaultValues['billingLastName']);
    const [isValidBillingAddress, setIsValidBillingAddress] = useState(!errors['billingAddress'] && !!defaultValues['billingAddress']);
    const [isValidBillingAddress2, setIsValidBillingAddress2] = useState(!errors['billingAddress2'] && !!defaultValues['billingAddress2']);
    const [isValidBillingZipCode, setIsValidBillingZipCode] = useState(!errors['billingZipCode'] && !!defaultValues['billingZipCode']);
    const [isValidBillingCity, setIsValidBillingCity] = useState(!errors['billingCity'] && !!defaultValues['billingCity']);
    const [isValidBillingState, setIsValidBillingState] = useState(!errors['billingState'] && !!defaultValues['billingState']);

    useEffect(() => {
        window.addEventListener('load', function () {
            document.getElementById('deliveryZipCode').addEventListener('click', () => {
                document.getElementById('deliveryZipCode').setAttribute('autocomplete', 'new-password');
            });
        });
    }, []);

    useEffect(() => {
        if (showOptionalDeliveryAddress) {
            document.getElementById('deliveryAddress2')?.addEventListener('blur', () => {
                setIsValidDeliveryAddress2(!errors['deliveryAddress2']);
            });

            return () => {
                document.getElementById('deliveryAddress2')?.removeEventListener('blur', () => { });
            }
        }
    }, [showOptionalDeliveryAddress]);

    useEffect(() => {
        if (checked === false) {
            document.getElementById('billingZipCode').addEventListener('click', () => {
                document.getElementById('billingZipCode').setAttribute('autocomplete', 'new-password');
            });
        }
    }, [checked]);

    useEffect(() => {
        if (showOptionalBillingAddress) {
            document.getElementById('billingAddress2')?.addEventListener('blur', () => {
                setIsValidBillingAddress2(!!errors['billingAddress2'] ? false : true);
            });

            return () => {
                document.getElementById('billingAddress2')?.removeEventListener('blur', () => { });
            }
        }
    }, [showOptionalBillingAddress]);

    //handle click on checkbox
    const clickOnCheckbox = () => {
        setChecked(checked => checked = !checked);
    }

    const clickOnSmsCheckbox = () => {
        setSmsChecked(smsChecked => smsChecked = !smsChecked);
        window.smsChecked=smsChecked;
    }

    //if checked reset the billing part of the form
    useEffect(() => {
        if (checked === true) {
            unregister('billingZipCode');
            reset({
                'billingFirstName': null,
                'billingLastName': null,
                'billingAddress': null,
                'billingAddress2': null,
                'billingCity': null,
                'billingCountry': null,
                'billingState': null,
                'billingZipCode': null
            });

            trigger('deliveryZipCode');

            if (!!document.getElementById('deliveryZipCode').value) {
                deliveryZipCode.current.value = document.getElementById('deliveryZipCode').value;
                setValue('deliveryZipCode', document.getElementById('deliveryZipCode').value);
                setIsValidDeliveryZipCode(true);
                trigger('deliveryZipCode');
            }

            if (!!document.getElementById('deliveryPhone').value) {
                deliveryPhone.current.value = document.getElementById('deliveryPhone').value;
                setValue('deliveryPhone', document.getElementById('deliveryPhone').value);
                setIsValidDeliveryPhone(true);
                trigger('deliveryPhone');
            }

            if (!!config.form && config.form.dob_field === true && !!document.getElementsByName('deliveryBirthday')[0].value) {
                setBirthdayValue(document.getElementsByName('deliveryBirthday')[0].value);
                setValue('deliveryBirthday', document.getElementsByName('deliveryBirthday')[0].value);
                setIsValidDeliveryBirthday(true);
                trigger('deliveryBirthday');
            }

        }

        /*@ts-expect-error*/
        setValue('use-address-for-billing', checked);

        if (checked === false && addRef === true) {

            setValue('billingFirstName', address.billingAddress.firstName);
            setValue('billingLastName', address.billingAddress.lastName);
            setValue('billingAddress', address.billingAddress.address1);
            setValue('billingAddress2', address.billingAddress.address2);
            setValue('billingCity', address.billingAddress.city);
            setValue('billingState', address.billingAddress.state);

            !!address.billingAddress.firstName ? setIsValidBillingFirstName(true) : setIsValidBillingFirstName(false);
            !!address.billingAddress.lastName ? setIsValidBillingLastName(true) : setIsValidBillingLastName(false);
            !!address.billingAddress.address1 ? setIsValidBillingAddress(true) : setIsValidBillingAddress(false);
            if (address.billingAddress.address2) {
                setIsValidBillingAddress2(true);
                setShowOptionalBillingAddress(true);
            } else {
                setIsValidBillingAddress2(false);
                setShowOptionalBillingAddress(false);
            }
            !!address.billingAddress.city ? setIsValidBillingCity(true) : setIsValidBillingCity(false);

            if (!!address && !!address.billingAddress && !!address.billingAddress.zipCode && !!billingZipCode.current) {
                billingZipCode.current.value = address.billingAddress.zipCode;
                setValue('billingZipCode', address.billingAddress.zipCode);
                setIsValidBillingZipCode(true);
            }

            if (config.autocomplete === 'on') {
                handleScriptLoad(autoComplete2, billingZipCode, 'billing');
                setAddRef(false);
            }
        }
    }, [checked]);

    useEffect(() => {
        setValue('subscribe-sms', smsChecked + "");
        localStorage.setItem('subscribe-sms', smsChecked + "");
    }, [smsChecked]);

    useEffect(() => {
        if (address !== null && !!address.deliveryAddress && !!address.deliveryAddress.email1 &&
            address.deliveryAddress.email1 !== 'undefined' && address.deliveryAddress.email1 !== undefined) {
            setChecked(getCheckboxValue(address));
            const str = defaultValues['deliveryFirstName'] + ' ' + defaultValues['deliveryLastName'] + ', ' + defaultValues['deliveryAddress'];
            setSelectedAddress(str);

            setValue('deliveryEmail', defaultValues['deliveryEmail']);
            setValue('deliveryFirstName', defaultValues['deliveryFirstName']);
            setValue('deliveryLastName', defaultValues['deliveryLastName']);
            setValue('deliveryAddress', defaultValues['deliveryAddress']);
            setValue('deliveryAddress2', defaultValues['deliveryAddress2']);
            setValue('deliveryCity', defaultValues['deliveryCity']);
            setValue('deliveryState', defaultValues['deliveryState']);
            setValue('deliveryCountry', 'US');
            if (!!config.form && config.form.dob_field === true) {
                setValue('deliveryBirthday', defaultValues['deliveryBirthday']);
                setBirthdayValue(defaultValues['deliveryBirthday']);
                setIsValidDeliveryBirthday(!!defaultValues['deliveryBirthday'] ? true : null);
            }

            controlAddressForSmartGlasses("deliveryZipCode", 'POSTAL_CODE');
            controlAddressForSmartGlasses('deliveryState', 'STATE_PROVINCE');

            setIsValidEmail(true);
            setIsValidDeliveryFirstName(true);
            setIsValidDeliveryLastName(true);
            setIsValidDeliveryAddress(true);

            if (!!defaultValues['deliveryAddress2']) {
                setIsValidDeliveryAddress2(true);
                setShowOptionalDeliveryAddress(true);
            } else {
                setIsValidBillingAddress2(false);
                setShowOptionalDeliveryAddress(false);
            }
            setIsValidDeliveryCity(true);

            setDefaultValueDeliveryState(!!defaultValues['deliveryState'] ? defaultValues['deliveryState'] : null);
            setDefaultValueBillingState(!!defaultValues['billingState'] ? defaultValues['billingState'] : null);
            if (!!address.deliveryAddress.phone1 && !!deliveryPhone.current) {
                deliveryPhone.current.value = address.deliveryAddress.phone1;
                setValue('deliveryPhone', address.deliveryAddress.phone1);
                setIsValidDeliveryPhone(true);
            }
            if (!!address.deliveryAddress.zipCode && !!deliveryZipCode.current) {
                deliveryZipCode.current.value = address.deliveryAddress.zipCode;
                setValue('deliveryZipCode', address.deliveryAddress.zipCode);
                setIsValidDeliveryZipCode(true);
            }

        }
    }, [address]);

    useEffect(() => {
        if (!!defaultValues['deliveryZipCode'] && !!defaultValues['deliveryState']) {
            controlAddressForSmartGlasses("deliveryZipCode", 'POSTAL_CODE');
            controlAddressForSmartGlasses('deliveryState', 'STATE_PROVINCE');
        }
    }, [defaultValues]);

    useEffect(() => {
        if (!!cart) {
            dispatch(actions.getAddress(config.mockRequests, config.hostname, cart.orderId));
        }
    }, [cart]);

    const controlAddressForSmartGlasses = (id: string, property: string) => {
        if (!!formattedOrderItems && document.getElementById(id)!=null) {
            const numberOfSmart = formattedOrderItems.filter(item => !!item.moCo.prod_hierarchy_1 && item.moCo.prod_hierarchy_1.toLowerCase() === 'electronics').length;
            if (numberOfSmart > 0) {
                const value = methods.getValues(id);

                const index = smartGlassesDenied.findIndex(smart => smart[property] === value && smart[property] !== '0');

                if (index !== -1) {
                    methods.setError(id, {
                        type: 'manual',
                        message: getLabelFallback('SMART_GLASSES_INVALID_' + property + '_LABEL')
                    });

                    if (property === 'POSTAL_CODE') {
                        setIsValidDeliveryZipCode(false);
                    } else {
                        setIsValidDeliveryState(false);
                    }
                } else {
                    if (property === 'POSTAL_CODE') {
                        setIsValidDeliveryZipCode(true);
                    } else {
                        setIsValidDeliveryState(true);
                    }
                }
            } else {
                if (property === 'POSTAL_CODE') {
                    setIsValidDeliveryZipCode(true);
                } else {
                    setIsValidDeliveryState(true);
                }
            }
        }
    }

    function handleScriptLoad(autoComplete, autoCompleteRef, type) {
        autoComplete = new window.google.maps.places.Autocomplete(
            autoCompleteRef.current,
            { types: ["(regions)"], componentRestrictions: { country: ["us", "pr"] } }
        );

        autoComplete.addListener("place_changed", () => {
            const place = autoComplete.getPlace();
            const zipCode = place.address_components.find(item => item.types[0] === 'postal_code').long_name;
            const city = place.address_components.find(item => item.types[0] === 'locality').long_name;
            const state = (place.address_components.find(item => item.types[0] === 'country').short_name == "PR") ? place.address_components.find(item => item.types[0] === 'country').short_name : place.address_components.find(item => item.types[0] === 'administrative_area_level_1').short_name;
            setValue(type + 'ZipCode', zipCode);
            setValue(type + 'State', state);
            setValue(type + 'City', city);

            document.getElementById(type + 'ZipCode').value = zipCode;
            document.getElementById(type + 'State').value = state;
            document.getElementById(type + 'City').value = city;

            if (type === 'delivery') {
                deliveryZipCode.current.value = zipCode;
                setDefaultValueDeliveryState(state);
                controlAddressForSmartGlasses("deliveryZipCode", 'POSTAL_CODE');
                controlAddressForSmartGlasses('deliveryState', 'STATE_PROVINCE');
                setIsValidDeliveryCity(true);
                setIsValidDeliveryState(true);
                trigger('deliveryZipCode');
                trigger('deliveryCity');
                trigger('deliveryState');
            } else {
                billingZipCode.current.value = zipCode;
                setDefaultValueBillingState(state);
                setIsValidBillingZipCode(true);
                setIsValidBillingCity(true);
                setIsValidBillingState(true);
                trigger('billingZipCode');
                trigger('billingCity');
                trigger('billingState');
            }
        }
        );
    }

    //load autocomplete
    useEffect(() => {
        if (config.autocomplete === 'on') {
            if (typeof (window.google) === 'undefined' && typeof (window.google.maps) === 'undefined' && typeof (window.google.maps.places) === 'undefined') {
                loadScript(
                    `https://maps.googleapis.com/maps/api/js?key=AIzaSyBYKlTpu53k5g-bkUSwS5ZHOJbttikH800&libraries=places`,
                    //`https://maps.googleapis.com/maps/api/js?key=AIzaSyDTDrO2IcsgqiSzz7D-7VihEdJllXPoHEM&libraries=places`,
                    () => {
                        handleScriptLoad(autoComplete, deliveryZipCode, 'delivery');
                    }
                );
            } else {
                handleScriptLoad(autoComplete, deliveryZipCode, 'delivery');
            }


            window.gm_authFailure = () => {
                //do not disable inputs if Google apis autocomplete gets error
                const inputs = document.querySelectorAll<HTMLInputElement>('.pac-target-input.gm-err-autocomplete:disabled');
                for (let input of Array.from(inputs)) {
                    input.disabled = false;
                    input.placeholder = "";
                }

                // google.maps.event.removeListener(placeChangedListener.address);
                // google.maps.event.clearInstanceListeners(autoComplete.address);

                console.error('General Error with Google Api ');
                return false;
            }

            return () => {
                //remove gm_authFailure method
                window.gm_authFailure = undefined;
            }
        }

    }, []);


    const formatBillingAddresses = (addresses) => {
        if (!addresses || addresses.length === 0) return [
            {
                code: getLabelFallback('EMPTY_ADDRESSES_SELECT_LABEL'),
                label: getLabelFallback('EMPTY_ADDRESSES_SELECT_LABEL'),
                disabled: true
            }
        ];

        return addresses.map((add) => {
            const str = add.firstName + ' ' + add.lastName + ', ' + add.address1;
            return {
                code: str,
                label: str,
                disabled: false
            }
        })
    }

    useEffect(() => {
        if (!!allBillingAddresses) {
            let formattedBillingAddresses = [];
            if (allBillingAddresses.length > 0) {
                formattedBillingAddresses = formatBillingAddresses(allBillingAddresses);
            }

            formattedBillingAddresses.unshift({
                code: getLabelFallback('ADD_NEW_ADDRESS_LABEL'),
                label: getLabelFallback('ADD_NEW_ADDRESS_LABEL'),
                disabled: false
            });

            setBillingAddresses(formattedBillingAddresses);

        }
    }, [allBillingAddresses]);

    const fillFormFieldFromAddress = (value: string) => {
        if (value === getLabelFallback('ADD_NEW_ADDRESS_LABEL')) {
            reset({
                'deliveryEmail': null,
                'deliveryFirstName': null,
                'deliveryLastName': null,
                'deliveryAddress': null,
                'deliveryAddress2': null,
                'deliveryCity': null,
                'deliveryCountry': null,
                'deliveryState': null,
                'deliveryZipCode': null,
                'deliveryBirthday': null,
                'deliveryPhone': null
            });

            deliveryPhone.current.value = null;
            deliveryZipCode.current.value = null;
            setDefaultValueDeliveryState(null);

            setIsValidEmail(null);
            setIsValidDeliveryFirstName(null);
            setIsValidDeliveryLastName(null);
            setIsValidDeliveryAddress(null);
            setIsValidDeliveryAddress2(null);
            setIsValidDeliveryCity(null);
            setIsValidDeliveryState(null);
            setIsValidDeliveryZipCode(null);

            if (!!config.form && config.form.dob_field) {
                setIsValidDeliveryBirthday(null);
                setBirthdayValue(null);
                trigger('deliveryBirthday');
            }
            setIsValidDeliveryPhone(null);
        } else {
            const index = billingAddresses.findIndex(b => b.code === value);

            reset({
                'deliveryEmail': null,
                'deliveryFirstName': null,
                'deliveryLastName': null,
                'deliveryAddress': null,
                'deliveryAddress2': null,
                'deliveryCity': null,
                'deliveryCountry': null,
                'deliveryState': null,
                'deliveryZipCode': null,
                'deliveryBirthday': null,
                'deliveryPhone': null
            });

            const birthdayFromCart = !!cart ? cart.orderItem.find(item => item.xitem_dateOfBirth) : null;

            const birthday = !!address.deliveryAddress.DATE_OF_BIRTH ? address.deliveryAddress.DATE_OF_BIRTH :
                !!cart && !!cart.orderItem && cart.orderItem.length > 0 ?
                    !!birthdayFromCart ? birthdayFromCart.xitem_dateOfBirth : null
                    : null;

            setValue('deliveryEmail', allBillingAddresses[index - 1].email1);
            setValue('deliveryFirstName', allBillingAddresses[index - 1].firstName);
            setValue('deliveryLastName', allBillingAddresses[index - 1].lastName);
            setValue('deliveryAddress', allBillingAddresses[index - 1].address1);
            setValue('deliveryAddress2', allBillingAddresses[index - 1].address2);
            setValue('deliveryCity', allBillingAddresses[index - 1].city);
            setValue('deliveryCountry', 'US');
            setValue('deliveryState', allBillingAddresses[index - 1].state);
            setDefaultValueDeliveryState(allBillingAddresses[index - 1].state);
            deliveryZipCode.current.value = allBillingAddresses[index - 1].zipCode;
            setValue('deliveryZipCode', allBillingAddresses[index - 1].zipCode);
            setValue('deliveryPhone', !!allBillingAddresses[index - 1].phone1 ? getFormattedPhone(allBillingAddresses[index - 1].phone1) : '');
            deliveryPhone.current.value = !!allBillingAddresses[index - 1].phone1 ? getFormattedPhone(allBillingAddresses[index - 1].phone1) : '';
            controlAddressForSmartGlasses("deliveryZipCode", 'POSTAL_CODE');
            controlAddressForSmartGlasses('deliveryState', 'STATE_PROVINCE');

            if (!!config.form && config.form.dob_field === true) {
                setBirthdayValue(birthday);
                setValue('deliveryBirthday', birthday);
                setIsValidDeliveryBirthday(!!birthday ? true : null);
            }

            setIsValidEmail(true);
            setIsValidDeliveryFirstName(true);
            setIsValidDeliveryLastName(true);
            setIsValidDeliveryAddress(true);
            setIsValidDeliveryAddress2(true);
            setIsValidDeliveryCity(true);
            setIsValidDeliveryState(true);
            setIsValidDeliveryZipCode(true);
            setIsValidDeliveryPhone(true);
        }

        setChecked(true);
    }

    useEffect(() => {
        if (addressErrors !== '') {
            if (addressErrors === 'nickname' || addressErrors === 'generic') {
                setHasError(addressErrors);
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            } else {
                if (addressErrors === 'all') {
                    ['ZipCode', 'State', 'City'].forEach(item => {
                        setError('delivery' + item, {
                            type: 'manual',
                            message: getLabelFallback('INVALID_' + item.toUpperCase() + '_ERROR_LABEL')
                        })
                    })
                } else if (addressErrors === 'puertorico_stella') {
                    setError('delivery' + 'ZipCode', {
                        type: 'manual',
                        message: getLabelFallback('INVALID_PR_ZIPCODE_ERROR_LABEL_1') + " \n" + getLabelFallback('INVALID_PR_ZIPCODE_ERROR_LABEL_2')
                    })
                } else if (addressErrors === 'stella_excluded') {
                    setError('delivery' + 'ZipCode', {
                        type: 'manual',
                        message: getLabelFallback('SMART_GLASSES_INVALID_ADDRESS_LABEL')
                    })
                } else {
                    setError('delivery' + addressErrors.charAt(0).toUpperCase() + addressErrors.slice(1), {
                        type: 'manual',
                        message: getLabelFallback('INVALID_' + addressErrors.toUpperCase() + '_ERROR_LABEL')
                    })
                }
            }
        }
    }, [addressErrors]);

    const handleValidateDate = (value) => {
        const newDate = new Date(value);
        const month = (newDate.getMonth() + 1).toString();
        const monthRight = month.length === 1 ? '0' + month : month;
        const day = newDate.getDate().toString().length === 1 ? '0' + newDate.getDate() : newDate.getDate().toString();
        if (!!parseInt(month) && !!parseInt(day) && !!newDate.getFullYear() && newDate.getFullYear() >= 1900) {
            const formattedDate = monthRight + '/' + day + '/' + (newDate.getFullYear());
            const isValid = validateDate(formattedDate);
            setIsValidDeliveryBirthday(isValid);
            return isValid;
        }
        return false;
    }

    const handleOnChangeBirthday = (value) => {
        setBirthdayValue(value);
        if (value !== null) {
            const newDate = new Date(value);
            const month = (newDate.getMonth() + 1).toString();
            const monthRight = month.length === 1 ? '0' + month : month;
            const day = newDate.getDate().toString().length === 1 ? '0' + newDate.getDate() : newDate.getDate().toString();
            if (!!parseInt(month) && !!parseInt(day) && !!newDate.getFullYear() && newDate.getFullYear() >= 1900) {
                const formattedDate = monthRight + '/' + day + '/' + (newDate.getFullYear());
                const isValid = validateDate(formattedDate);
                setIsValidBillingAddress(isValid);
                if (isValid) {
                    clearErrors('deliveryBirthday');
                    setIsValidDeliveryBirthday(true);
                } else {
                    setError('deliveryBirthday', {
                        type: 'manual',
                        message: getLabelFallback('INVALID_FIELD_VALUE').replace('{FIELD_NAME}', getLabelFallback('DATE_OF_BIRTH_LABEL').toLocaleLowerCase()),
                    });
                    setIsValidDeliveryBirthday(false);
                }
            } else {
                setError('deliveryBirthday', {
                    type: 'manual',
                    message: getLabelFallback('INVALID_FIELD_VALUE').replace('{FIELD_NAME}', getLabelFallback('DATE_OF_BIRTH_LABEL').toLocaleLowerCase()),
                });
                setIsValidDeliveryBirthday(false);
            }
        } else {
            setError('deliveryBirthday', {
                type: 'manual',
                message: getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', getLabelFallback('DATE_OF_BIRTH_LABEL').toLocaleLowerCase()),
            });
            setIsValidDeliveryBirthday(false);
        }

    }

    useEffect(() => {
        const err = document.getElementsByClassName('not-valid');
        const el = Array.from(err)?.find(e => e.nodeName === 'INPUT');
        setFocusedField(el);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    useEffect(() => {
        focusedField?.focus();
    }, [focusedField]);

    return (
        <div>
            {(hasError === 'nickname' || hasError === 'generic') &&
                <TextBanner classes={"isError"}>
                    <div className="cart-error">
                        <span>{getLabelFallback(hasError.toUpperCase() + '_ERROR_LABEL')}</span>
                    </div>
                </TextBanner>
            }
            <FormProvider {...methods}>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    setFocusedField(null);
                    handleSubmit(onSubmitAddress)();
                }}
                >
                    {!!buyerName && billingAddresses.length > 1 &&
                        <div className="delivery-address-list-container">
                            <CustomSelect
                                id={'all-addresses-select'}
                                inputId={'all-addresses'}
                                placeholder={getLabelFallback('SELECT_ADDRESS_PLACEHOLDER')}
                                topLabel={getLabelFallback('SELECT_ADDRESS_TOP_LABEL')}
                                options={billingAddresses}
                                onChange={(value) => {
                                    methods.setValue('all-addresses', value)
                                    fillFormFieldFromAddress(value);
                                }}
                                defaultValue={selectedAddress}
                                showIcon={false}
                            />
                            <input {...methods.register('all-addresses')} type="hidden" name="all-addresses" id="all-addresses" />
                        </div>
                    }
                    <div className="delivery-address-form">
                        <div className={`input custom ${errors['deliveryEmail'] ? 'not-valid' : isValidEmail ? 'valid' : ''}`}>
                            <input
                                {...register('deliveryEmail', {
                                    required: true, pattern: {
                                        value: FIELD_PATTERN.EMAIL,
                                        message: getLabelFallback('EMAIL_ERROR_LABEL')
                                    }, validate: (value) => {
                                        if (!!value) {
                                            setIsValidEmail(true);
                                        } else {
                                            setIsValidEmail(false);
                                        }
                                        return !!value;
                                    }
                                })}
                                name={'deliveryEmail'}
                                id={'deliveryEmail'}
                                maxLength={64}
                                placeholder={getLabelFallback('DELIVERY_EMAIL_LABEL')}
                                className={`input-element ${errors['deliveryEmail'] ? 'not-valid' : isValidEmail ? 'valid' : ''}`}
                                onKeyPress={(e) => { onKeyPressEmail(e) }}
                                aria-invalid={errors['deliveryEmail'] ? 'true' : 'false'}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_EMAIL_LABEL')}</label>
                            <div className="input-icons">
                                <div className="input-icon">{errors['deliveryEmail'] ? <Error /> : isValidEmail ? <Success /> : ''}</div>
                            </div>
                            {!!errors['deliveryEmail'] && <span className="input-error">{errors['deliveryEmail'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'e-mail')}</span>}
                        </div>

                        <div className={`input custom ${errors['deliveryFirstName'] ? 'not-valid' : isValidDeliveryFirstName ? 'valid' : ''}`}>
                            <input
                                {...register('deliveryFirstName', {
                                    required: true,
                                    validate: (value) => {
                                        if (!!value) {
                                            setIsValidDeliveryFirstName(true);
                                        } else {
                                            setIsValidDeliveryFirstName(false);
                                        }

                                        return !!value;
                                    }
                                })}
                                name={'deliveryFirstName'}
                                id={'deliveryFirstName'}
                                maxLength={32}
                                placeholder={getLabelFallback('DELIVERY_FIRST_NAME_LABEL')}
                                className={`input-element ${errors['deliveryFirstName'] ? 'not-valid' : isValidDeliveryFirstName ? 'valid' : ''}`}
                                onKeyPress={(e) => onKeyPressAlpha(e)}
                                aria-invalid={errors['deliveryFirstName'] ? 'true' : 'false'}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_FIRST_NAME_LABEL')}</label>
                            <div className="input-icons">
                                <div className="input-icon">{errors['deliveryFirstName'] ? <Error /> : isValidDeliveryFirstName ? <Success /> : ''}</div>
                            </div>
                            {!!errors['deliveryFirstName'] && <span className="input-error">{errors['deliveryFirstName'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'first name')}</span>}
                        </div>

                        <div className={`input custom ${errors['deliveryLastName'] ? 'not-valid' : isValidDeliveryLastName ? 'valid' : ''}`}>
                            <input
                                {...register('deliveryLastName', {
                                    required: true,
                                    validate: (value) => {
                                        if (!!value) {
                                            setIsValidDeliveryLastName(true);
                                        } else {
                                            setIsValidDeliveryLastName(false);
                                        }

                                        return !!value;
                                    }
                                })}
                                name={'deliveryLastName'}
                                id={'deliveryLastName'}
                                maxLength={32}
                                placeholder={getLabelFallback('DELIVERY_LAST_NAME_LABEL')}
                                className={`input-element ${errors['deliveryLastName'] ? 'not-valid' : isValidDeliveryLastName ? 'valid' : ''}`}
                                onKeyPress={(e) => onKeyPressAlpha(e)}
                                aria-invalid={errors['deliveryLastName'] ? 'true' : 'false'}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_LAST_NAME_LABEL')}</label>
                            <div className="input-icons">
                                <div className="input-icon">{errors['deliveryLastName'] ? <Error /> : isValidDeliveryLastName ? <Success /> : ''}</div>
                            </div>
                            {!!errors['deliveryLastName'] && <span className="input-error">{errors['deliveryLastName'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'last name')}</span>}
                        </div>

                        {!!config.form && config.form.dob_field === true &&
                            <Controller
                                name={'deliveryBirthday'}
                                defaultValue={!!birthdayValue ? birthdayValue : null}
                                control={control}
                                rules={{ required: true, validate: (value) => handleValidateDate(value) || getLabelFallback('INVALID_FIELD_VALUE').replace('{FIELD_NAME}', getLabelFallback('DATE_OF_BIRTH_LABEL').toLocaleLowerCase()) }}
                                render={({ field }) => (
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DesktopDatePicker
                                            value={field.value}
                                            onChange={(e) => { field.onChange(e); handleOnChangeBirthday(e); }}
                                            disableFuture={true}
                                            label={getLabelFallback('DATE_OF_BIRTH_LABEL')}
                                            components={{
                                                OpenPickerIcon: () => { return <CalendarIcon /> }
                                            }}
                                            mask={"__/__/____"}
                                            inputFormat={'MM/dd/yyyy'}
                                            renderInput={({ inputRef, inputProps, InputProps }) => (
                                                <div className={`calendar-input`}>
                                                    <div className="calendar-input-container">
                                                        <input
                                                            ref={inputRef}
                                                            name={'deliveryBirthday'}
                                                            type="text"
                                                            className={`calendar-input-element  ${errors['deliveryBirthday'] ? 'not-valid' : isValidDeliveryBirthday ? 'valid' : ''}`}
                                                            aria-invalid={errors['deliveryBirthday'] ? 'true' : 'false'}
                                                            {...inputProps}
                                                            placeholder={getLabelFallback('DATE_OF_BIRTH_LABEL')}
                                                            onKeyPress={(e) => onKeyPressOnlyNumber(e)}
                                                            maxLength={10}
                                                        />
                                                        <label className="calendar-input-element-label">{getLabelFallback('DATE_OF_BIRTH_LABEL')}</label>
                                                        <div className="calendar-input-element-calendar">
                                                            <div className="calendar-input-icon">{errors['deliveryBirthday'] ? <Error /> : isValidDeliveryBirthday ? <Success /> : ''}</div>
                                                            {InputProps?.endAdornment}
                                                        </div>
                                                    </div>
                                                    <span className="calendar-input-element-claim">{getLabelFallback('DATE_OF_BIRTH_CLAIM')}</span>
                                                    {errors['deliveryBirthday'] && <span className="input-error">{errors['deliveryBirthday'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', getLabelFallback('DATE_OF_BIRTH_LABEL').toLocaleLowerCase())}</span>}
                                                </div>
                                            )}
                                        />
                                    </LocalizationProvider>
                                )}
                            />
                        }


                        <div className={`input custom big ${errors['deliveryPhone'] ? 'not-valid' : isValidDeliveryPhone ? 'valid' : ''}`}>
                            <input
                                type={'tel'}
                                ref={mergeRefs(deliveryPhone, imaskPhoneNumber.ref, register('deliveryPhone', {
                                    required: true,
                                    pattern: {
                                        value: /^\(\d{3}\) \d{3}-\d{4}/,
                                        message: getLabelFallback('INVALID_FIELD_VALUE').replace('{FIELD_NAME}', 'phone number')
                                    },
                                    validate: (value) => {
                                        if (!!value) {
                                            setIsValidDeliveryPhone(true);
                                        } else {
                                            setIsValidDeliveryPhone(false);
                                        }

                                        return !!value;
                                    }
                                }))}
                                name={'deliveryPhone'}
                                id={'deliveryPhone'}
                                maxLength={15}
                                placeholder={getLabelFallback('DELIVERY_PHONE_LABEL')}
                                className={`input-element ${errors['deliveryPhone'] ? 'not-valid' : isValidDeliveryPhone ? 'valid' : ''}`}
                                onChange={(e) => {
                                    setValue('deliveryPhone', e.target.value);
                                    deliveryPhone.current.value = e.target.value;
                                    document.getElementById('deliveryPhone').value = e.target.value;
                                }}
                                aria-invalid={errors['deliveryPhone'] ? 'true' : 'false'}
                                onBlur={(e) => {
                                    setValue('deliveryPhone', e.target.value);
                                    deliveryPhone.current.value = e.target.value;
                                    document.getElementById('deliveryPhone').value = e.target.value;
                                    trigger('deliveryPhone');
                                }}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_PHONE_LABEL')}</label>
                            <span className="input-claim">{getLabelFallback('DELIVERY_PHONE_CLAIM')}</span>
                            <div className="input-icons">
                                <div className="input-icon">{errors['deliveryPhone'] ? <Error /> : isValidDeliveryPhone ? <Success /> : ''}</div>
                            </div>
                            {!!errors['deliveryPhone'] && <span className="input-error">{errors['deliveryPhone'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'phone')}</span>}
                        </div>

                        <div className={`input custom big ${errors['deliveryAddress'] ? 'not-valid' : isValidDeliveryAddress ? 'valid' : ''}`}>
                            <input
                                {...register('deliveryAddress', {
                                    required: true,
                                    validate: (value) => {
                                        if (!!value) {
                                            setIsValidDeliveryAddress(true);
                                        } else {
                                            setIsValidDeliveryAddress(false);
                                        }

                                        return !!value;
                                    }
                                })}
                                name={'deliveryAddress'}
                                id={'deliveryAddress'}
                                maxLength={50}
                                placeholder={getLabelFallback('DELIVERY_ADDRESS_LABEL')}
                                className={`input-element ${errors['deliveryAddress'] ? 'not-valid' : isValidDeliveryAddress ? 'valid' : ''}`}
                                onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                aria-invalid={errors['deliveryAddress'] ? 'true' : 'false'}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_ADDRESS_LABEL')}</label>
                            <span className="input-claim">{getLabelFallback('DELIVERY_ADDRESS_CLAIM')}</span>
                            <div className="input-icons">
                                <div className="input-icon">{errors['deliveryAddress'] ? <Error /> : isValidDeliveryAddress ? <Success /> : ''}</div>
                            </div>
                            {!!errors['deliveryAddress'] && <span className="input-error">{errors['deliveryAddress'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'street address')}</span>}
                        </div>

                        {!showOptionalDeliveryAddress &&
                            <div className="delivery-address-form-details">
                                <button className="primary-button" onClick={(e) => { e.preventDefault(); setShowOptionalDeliveryAddress(showOptionalDeliveryAddress => showOptionalDeliveryAddress = !showOptionalDeliveryAddress); }}>{getLabelFallback('DELIVERY_DETAILS_LABEL')}</button>
                            </div>
                        }

                        {showOptionalDeliveryAddress &&
                            <div className={`input custom big ${errors['deliveryAddress2'] ? 'not-valid' : isValidDeliveryAddress2 ? 'valid' : ''}`}>
                                <input
                                    {...register('deliveryAddress2', {
                                        validate: (value) => { if (!!value) { setIsValidDeliveryAddress2(true); return !!value; } return true; }
                                    })}
                                    name={'deliveryAddress2'}
                                    id={'deliveryAddress2'}
                                    maxLength={50}
                                    placeholder={getLabelFallback('DELIVERY_ADDRESS2_LABEL')}
                                    className={`input-element ${errors['deliveryAddress2'] ? 'not-valid' : isValidDeliveryAddress2 ? 'valid' : ''}`}
                                    onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                    aria-invalid={errors['deliveryAddress2'] ? 'true' : 'false'}
                                />
                                <label className="input-label">{getLabelFallback('DELIVERY_ADDRESS2_LABEL')}</label>
                                <div className="input-icons">
                                    <div className="input-icon">{errors['deliveryAddress2'] ? <Error /> : isValidDeliveryAddress2 ? <Success /> : ''}</div>
                                </div>
                            </div>
                        }

                        <div className={`input custom big ${errors['deliveryZipCode'] ? 'not-valid' : isValidDeliveryZipCode ? 'valid' : ''}`}>
                            <input
                                type={'number'}
                                ref={mergeRefs(deliveryZipCode, register('deliveryZipCode', { required: true, validate: value => !value.startsWith('000') || "The Zipcode is invalid" }))}
                                name={'deliveryZipCode'}
                                id={'deliveryZipCode'}
                                maxLength={5}
                                placeholder={getLabelFallback('DELIVERY_ZIPCODE_LABEL')}
                                className={`input-element ${errors['deliveryZipCode'] ? 'not-valid' : isValidDeliveryZipCode ? 'valid' : ''}`}
                                onChange={(e) => {
                                    setValue('deliveryZipCode', e.target.value)
                                    deliveryZipCode.current.value = e.target.value;
                                    document.getElementById('deliveryZipCode').value = e.target.value;
                                }}
                                onKeyPress={(e) => onKeyPressOnlyNumber(e)}
                                aria-invalid={errors['deliveryZipCode'] ? 'true' : 'false'}
                                onKeyUp={() => controlAddressForSmartGlasses("deliveryZipCode", 'POSTAL_CODE')}
                                onBlur={(e) => {
                                    setValue('deliveryZipCode', e.target.value)
                                    deliveryZipCode.current.value = e.target.value;
                                    document.getElementById('deliveryZipCode').value = e.target.value;
                                    //trigger('deliveryZipCode');
                                }}
                            />
                            <label className="input-label">{getLabelFallback('DELIVERY_ZIPCODE_LABEL')}</label>
                            <div className="input-icons">
                                <div className="input-icon">{errors['deliveryZipCode'] ? <Error /> : isValidDeliveryZipCode ? <Success /> : ''}</div>
                            </div>
                            {!!errors['deliveryZipCode'] && <span className="input-error">{errors['deliveryZipCode'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'zipcode')}</span>}
                        </div>
                        {
                            watchDeliveryZipCode && isValidDeliveryZipCode &&
                            <div className="delivery-address-form-row">
                                <div className={`input custom ${errors['deliveryCity'] ? 'not-valid' : isValidDeliveryCity ? 'valid' : ''}`}>
                                    <input
                                        {...register('deliveryCity', {
                                            required: true,
                                            validate: (value) => {
                                                if (!!value) {
                                                    setIsValidDeliveryCity(true);
                                                } else {
                                                    setIsValidDeliveryCity(false);
                                                }

                                                return !!value;
                                            }
                                        })}
                                        name={'deliveryCity'}
                                        id={'deliveryCity'}
                                        maxLength={128}
                                        placeholder={getLabelFallback('DELIVERY_CITY_LABEL')}
                                        className={`input-element ${errors['deliveryCity'] ? 'not-valid' : isValidDeliveryCity ? 'valid' : ''}`}
                                        onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                        aria-invalid={errors['deliveryCity'] ? 'true' : 'false'}
                                    />
                                    <label className="input-label">{getLabelFallback('DELIVERY_CITY_LABEL')}</label>
                                    <div className="input-icons">
                                        <div className="input-icon">{errors['deliveryCity'] ? <Error /> : isValidDeliveryCity ? <Success /> : ''}</div>
                                    </div>
                                    {!!errors['deliveryCity'] && <span className="input-error">{errors['deliveryCity'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'city')}</span>}
                                </div>

                                <CustomSelect
                                    id={"deliveryStateSelect"}
                                    inputId={"deliveryState"}
                                    placeholder={getLabelFallback('DELIVERY_STATE_LABEL')}
                                    topLabel={getLabelFallback('DELIVERY_STATE_LABEL')}
                                    options={states}
                                    onChange={(value) => setValue('deliveryState', value)}
                                    controlValue={() => controlAddressForSmartGlasses('deliveryState', 'STATE_PROVINCE')}
                                    defaultValue={defaultValueDeliveryState}
                                    aria-invalid={errors['deliveryState'] ? 'true' : 'false'}
                                    clearErrorProps={() => { clearErrors('deliveryState'); }}
                                    errorProps={errors['deliveryState']}
                                />

                                <input {...register('deliveryState', { required: true })} type="hidden" name="deliveryState" id="deliveryState" />
                            </div>
                        }

                        {!!selectedShippingMode && selectedShippingMode.shipModeCode.toLowerCase() === 'speedy rx' && !!document.getElementById('deliveryState') && !!document.getElementById('deliveryState').value && (document.getElementById('deliveryState').value === 'AK' || document.getElementById('deliveryState').value === 'HI') && 
                            <div className='speedy-info'>
                                <div><InfoIcon /></div>
                                <span>{getLabelFallback('SPEEDY_INFO_ALASKA_HAWAII')}</span>
                            </div>
                        }

                        <Checkbox id={"use-address-for-billing"}
                            label={getLabelFallback('BILLING_CHECKBOX_LABEL')}
                            checked={checked}
                            onChange={() => { clickOnCheckbox() }} />

                        {!checked &&
                            <>
                                <h3 className="delivery-address-form-title">{getLabelFallback('BILLING_ADDRESS_LABEL')}</h3>
                                <div className={`input custom ${errors['billingFirstName'] ? 'not-valid' : isValidBillingFirstName ? 'valid' : ''}`}>
                                    <input
                                        {...register('billingFirstName', {
                                            required: true,
                                            validate: (value) => {
                                                if (!!value) {
                                                    setIsValidBillingFirstName(true);
                                                } else {
                                                    setIsValidBillingFirstName(false);
                                                }

                                                return !!value;
                                            }
                                        })}
                                        name={'billingFirstName'}
                                        id={'billingFirstName'}
                                        maxLength={32}
                                        placeholder={getLabelFallback('BILLING_FIRST_NAME_LABEL')}
                                        className={`input-element ${errors['billingFirstName'] ? 'not-valid' : isValidBillingFirstName ? 'valid' : ''}`}
                                        onKeyPress={(e) => onKeyPressAlpha(e)}
                                        aria-invalid={errors['billingFirstName'] ? 'true' : 'false'}
                                    />
                                    <label className="input-label">{getLabelFallback('BILLING_FIRST_NAME_LABEL')}</label>
                                    <div className="input-icons">
                                        <div className="input-icon">{errors['billingFirstName'] ? <Error /> : isValidBillingFirstName ? <Success /> : ''}</div>
                                    </div>
                                    {!!errors['billingFirstName'] && <span className="input-error">{errors['billingFirstName'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'first name')}</span>}
                                </div>

                                <div className={`input custom ${errors['billingLastName'] ? 'not-valid' : isValidBillingLastName ? 'valid' : ''}`}>
                                    <input
                                        {...register('billingLastName', {
                                            required: true,
                                            validate: (value) => {
                                                if (!!value) {
                                                    setIsValidBillingLastName(true);
                                                } else {
                                                    setIsValidBillingLastName(false);
                                                }

                                                return !!value;
                                            }
                                        })}
                                        name={'billingLastName'}
                                        id={'billingLastName'}
                                        maxLength={32}
                                        placeholder={getLabelFallback('BILLING_LAST_NAME_LABEL')}
                                        className={`input-element ${errors['billingLastName'] ? 'not-valid' : isValidBillingLastName ? 'valid' : ''}`}
                                        onKeyPress={(e) => onKeyPressAlpha(e)}
                                        aria-invalid={errors['billingLastName'] ? 'true' : 'false'}
                                    />
                                    <label className="input-label">{getLabelFallback('BILLING_LAST_NAME_LABEL')}</label>
                                    <div className="input-icons">
                                        <div className="input-icon">{errors['billingLastName'] ? <Error /> : isValidBillingLastName ? <Success /> : ''}</div>
                                    </div>
                                    {!!errors['billingLastName'] && <span className="input-error">{errors['billingLastName'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'last name')}</span>}
                                </div>

                                <div className={`input custom big ${errors['billingAddress'] ? 'not-valid' : isValidBillingAddress ? 'valid' : ''}`}>
                                    <input
                                        {...register('billingAddress', {
                                            required: true,
                                            validate: (value) => {
                                                if (!!value) {
                                                    setIsValidBillingAddress(true);
                                                } else {
                                                    setIsValidBillingAddress(false);
                                                }

                                                return !!value;
                                            }
                                        })}
                                        name={'billingAddress'}
                                        id={'billingAddress'}
                                        maxLength={50}
                                        placeholder={getLabelFallback('BILLING_ADDRESS1_LABEL')}
                                        className={`input-element ${errors['billingAddress'] ? 'not-valid' : isValidBillingAddress ? 'valid' : ''}`}
                                        onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                        aria-invalid={errors['billingAddress'] ? 'true' : 'false'}

                                    />
                                    <label className="input-label">{getLabelFallback('BILLING_ADDRESS1_LABEL')}</label>
                                    <div className="input-icons">
                                        <div className="input-icon">{errors['billingAddress'] ? <Error /> : isValidBillingAddress ? <Success /> : ''}</div>
                                    </div>
                                    {!!errors['billingAddress'] && <span className="input-error">{errors['billingAddress'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'street address')}</span>}
                                </div>

                                {!showOptionalBillingAddress &&
                                    <div className="delivery-address-form-details">
                                        <button className="primary-button" onClick={(e) => { e.preventDefault(); setShowOptionalBillingAddress(showOptionalBillingAddress => showOptionalBillingAddress = !showOptionalBillingAddress); }}>{getLabelFallback('DELIVERY_DETAILS_LABEL')}</button>
                                    </div>
                                }

                                {showOptionalBillingAddress &&
                                    <div className={`input custom big ${errors['billingAddress2'] ? 'not-valid' : isValidBillingAddress2 ? 'valid' : ''}`}>
                                        <input
                                            {...register('billingAddress2', {
                                                validate: (value) => { if (!!value) { setIsValidBillingAddress2(true); return !!value; } return true; }
                                            })}
                                            name={'billingAddress2'}
                                            id={'billingAddress2'}
                                            maxLength={50}
                                            placeholder={getLabelFallback('BILLING_ADDRESS2_LABEL')}
                                            className={`input-element ${errors['billingAddress2'] ? 'not-valid' : isValidBillingAddress2 ? 'valid' : ''}`}
                                            onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                            aria-invalid={errors['billingAddress2'] ? 'true' : 'false'}
                                        />
                                        <label className="input-label">{getLabelFallback('BILLING_ADDRESS2_LABEL')}</label>
                                        <div className="input-icons">
                                            <div className="input-icon">{errors['billingAddress2'] ? <Error /> : isValidBillingAddress2 ? <Success /> : ''}</div>
                                        </div>
                                        {!!errors['billingAddress2'] && <span className="input-error">{errors['billingAddress2'].message}</span>}
                                    </div>
                                }

                                <div className={`input custom big ${errors['billingZipCode'] ? 'not-valid' : isValidBillingZipCode ? 'valid' : ''}`}>
                                    <input
                                        type={'tel'}
                                        ref={mergeRefs(billingZipCode, register('billingZipCode', { required: true, validate: value => !value.startsWith('000') || "The Zipcode is invalid" }))}
                                        name={'billingZipCode'}
                                        id={'billingZipCode'}
                                        maxLength={5}
                                        placeholder={getLabelFallback('BILLING_ZIPCODE_LABEL')}
                                        className={`input-element ${errors['billingZipCode'] ? 'not-valid' : isValidBillingZipCode ? 'valid' : ''}`}
                                        onKeyPress={(e) => onKeyPressOnlyNumber(e)}
                                        aria-invalid={errors['billingZipCode'] ? 'true' : 'false'}
                                        onChange={(e) => {
                                            setValue('billingZipCode', e.target.value)
                                            billingZipCode.current.value = e.target.value;
                                            document.getElementById('billingZipCode').value = e.target.value;
                                        }}
                                        onBlur={(e) => {
                                            setValue('billingZipCode', e.target.value)
                                            billingZipCode.current.value = e.target.value;
                                            document.getElementById('billingZipCode').value = e.target.value;
                                            trigger('billingZipCode');
                                        }}
                                    />
                                    <label className="input-label">{getLabelFallback('BILLING_ZIPCODE_LABEL')}</label>
                                    <div className="input-icons">
                                        <div className="input-icon">{errors['billingZipCode'] ? <Error /> : isValidBillingZipCode ? <Success /> : ''}</div>
                                    </div>
                                    {!!errors['billingZipCode'] && <span className="input-error">{errors['billingZipCode'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'zipcode')}</span>}
                                </div>

                                <div className="delivery-address-form-row">
                                    <div className={`input custom ${errors['billingCity'] ? 'not-valid' : isValidBillingCity ? 'valid' : ''}`}>
                                        <input
                                            {...register('billingCity', {
                                                required: true,
                                                validate: (value) => {
                                                    if (!!value) {
                                                        setIsValidBillingCity(true);
                                                    } else {
                                                        setIsValidBillingCity(false);
                                                    }

                                                    return !!value;
                                                }
                                            })}
                                            name={'billingCity'}
                                            id={'billingCity'}
                                            maxLength={128}
                                            placeholder={getLabelFallback('BILLING_CITY_LABEL')}
                                            className={`input-element ${errors['billingCity'] ? 'not-valid' : isValidBillingCity ? 'valid' : ''}`}
                                            onKeyPress={(e) => onKeyPressAlphaNumeric(e)}
                                            aria-invalid={errors['billingCity'] ? 'true' : 'false'}
                                        />
                                        <label className="input-label">{getLabelFallback('BILLING_CITY_LABEL')}</label>
                                        <div className="input-icons">
                                            <div className="input-icon">{errors['billingCity'] ? <Error /> : isValidBillingCity ? <Success /> : ''}</div>
                                        </div>
                                        {!!errors['billingCity'] && <span className="input-error">{errors['billingCity'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', 'city')}</span>}
                                    </div>

                                    <CustomSelect
                                        id={"billingStateSelect"}
                                        inputId={"billingState"}
                                        placeholder={getLabelFallback('DELIVERY_STATE_LABEL')}
                                        topLabel={getLabelFallback('BILLING_STATE_LABEL')}
                                        options={states}
                                        onChange={(value) => setValue('billingState', value)}
                                        defaultValue={defaultValueBillingState}
                                        aria-invalid={errors['billingState'] ? 'true' : 'false'}
                                    />
                                    <input {...register('billingState', { required: true })} type="hidden" id="billingState" />
                                </div>
                            </>
                        }

                        {config.sms.enabled === true &&
                            <div className='sms-checkbox'>
                                <Checkbox id={"subscribe-sms"}
                                    label={getLabelFallback('SMS_CHECKBOX_LABEL')}
                                    checked={smsChecked}
                                    onChange={() => { clickOnSmsCheckbox() }} />
                            </div>
                        }

                        {config.sms.enabled === true &&
                            <div className="sms-terms-conditions">
                                <span dangerouslySetInnerHTML={{
                                    __html: getLabelFallback('SMS_TERMS_LABEL')
                                        .replace('{CONDITIONS}', '<a class="terms-privacy" target="_blank" aria-label="' + getLabelFallback('SMS_TERMS_CONDITION_LABEL') + '" href="' + config.linkConditionUseSale + '">' + getLabelFallback('SMS_TERMS_CONDITION_LABEL') + '</a> & <a class="terms-privacy" target="_blank" aria-label="' + getLabelFallback('SMS_TERMS_PRIVACY_LABEL') + '" href="/gl-us/privacy-policy">' + getLabelFallback('SMS_TERMS_PRIVACY_LABEL') + '</a>')
                                }}>
                                </span>
                            </div>
                        }

                        <button data-element-id="Form_Proceed" className="violet-button" type="submit">{getLabelFallback('PAYMENT_BUTTON_LABEL')}</button>

                        <div className="delivery-address-form-claim">
                            <span dangerouslySetInnerHTML={{
                                __html: getLabelFallback('PAYMENT_ACCEPTANCE_RIGHTS_LABEL')
                                    .replace('{SITE_NAME}', getLabelFallback('SITE_NAME'))
                                    .replace('{CONDITIONS}', '<a class="primary-button" target="_blank" aria-label="' + getLabelFallback('CONDITIONS_USE_SALE_LABEL') + '" href="' + config.linkConditionUseSale + '">' + getLabelFallback('CONDITIONS_USE_SALE_LABEL') + '</a>')
                                    .replace('{PRIVACY}', '<a class="primary-button" target="_blank" aria-label="' + getLabelFallback('PRIVACY_NOTICE_LABEL') + '" href="' + config.linkPrivacyNotice + '">' + getLabelFallback('PRIVACY_NOTICE_LABEL') + '</a>')
                                    .replace('{COOKIES}', '<a class="primary-button" target="_blank" aria-label="' + getLabelFallback('COOKIES_LABEL') + '" href="' + config.linkCookieAndInternetAdvertising + '">' + getLabelFallback('COOKIES_LABEL') + '</a>')
                            }}>
                            </span>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}

export default DeliveryAddressForm;

const getDefaultValues = (address, cart) => {
    if (address === null) return {};

    const birthdayFromCart = !!cart ? cart.orderItem.find(item => item.xitem_dateOfBirth) : null;

    const birthday = !!address.deliveryAddress.DATE_OF_BIRTH ? address.deliveryAddress.DATE_OF_BIRTH :
        !!cart && !!cart.orderItem && cart.orderItem.length > 0 ?
            !!birthdayFromCart ? birthdayFromCart.xitem_dateOfBirth : null
            : null;

    return {
        'deliveryEmail': address.deliveryAddress.email1,
        'deliveryFirstName': address.deliveryAddress.firstName,
        'deliveryLastName': address.deliveryAddress.lastName,
        'deliveryAddress': address.deliveryAddress.address1,
        'deliveryAddress2': address.deliveryAddress.address2,
        'deliveryBirthday': birthday,
        'deliveryCity': address.deliveryAddress.city,
        'deliveryPhone': !!address.deliveryAddress.phone1 ? getFormattedPhone(address.deliveryAddress.phone1) : '',
        'deliveryState': address.deliveryAddress.state,
        'deliveryZipCode': address.deliveryAddress.zipCode,
        'billingFirstName': address.billingAddress.firstName,
        'billingLastName': address.billingAddress.lastName,
        'billingAddress': address.billingAddress.address1,
        'billingAddress2': address.billingAddress.address2,
        'billingCity': address.billingAddress.city,
        'billingState': address.billingAddress.state,
        'billingZipCode': address.billingAddress.zipCode
    }
}

const getCheckboxValue = (address) => {
    if (address === null) return true;

    if (
        address.deliveryAddress.firstName === address.billingAddress.firstName && address.deliveryAddress.firstName !== '' && address.deliveryAddress.firstName !== 'undefined' &&
        address.deliveryAddress.lastName === address.billingAddress.lastName && address.deliveryAddress.lastName !== '' && address.deliveryAddress.lastName !== 'undefined' &&
        address.deliveryAddress.city === address.billingAddress.city && address.deliveryAddress.city !== '' && address.deliveryAddress.city !== 'undefined' &&
        address.deliveryAddress.zipCode === address.billingAddress.zipCode && address.deliveryAddress.zipCode !== '' && address.deliveryAddress.zipCode !== 'undefined' &&
        address.deliveryAddress.address1 === address.billingAddress.address1 && address.deliveryAddress.address1 !== '' && address.deliveryAddress.address1 !== 'undefined' &&
        address.deliveryAddress.state === address.billingAddress.state && address.deliveryAddress.state !== '' && address.deliveryAddress.state !== 'undefined'
    ) {
        return true;
    } else {
        return false;
    }
}

const getSmsCheckboxValue = () => {
    if (localStorage.getItem('subscribe-sms') == 'true') {
        return true;
    } else {
        return false;
    }
}